import { useState, useEffect } from 'react'
import { createRedeemCode, getAllRedeemCodes } from 'src/api/redeemcodes'
import {
  Divider,
  Table,
  Spin,
  Tag,
  Select,
  Modal,
  Button,
  Form,
  message,
  Row,
  Col,
  Typography,
} from 'antd'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { isoToTimestamp } from 'src/utils/timestamp'
import { fetchCountryProvider } from 'src/api/authen'
import { REDEEMCODESUBSCRIPTIONTYPE, REDEEMFLAG } from 'src/constants'

const { Title } = Typography
const RedeemCodePage = () => {
  const [loading, setLoading] = useState(true)
  const [selectedCountryValue, setSelectedCountryValue] = useState<
    string | undefined
  >(null)
  const [redeemed, setRedeemed] = useState('')
  const [subscriptionType, setSubscriptionType] = useState('')
  const [transformedData, setTransformedData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [countries, setCountries] = useState([])
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [form] = Form.useForm()

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current)
    setPageSize(pagination.pageSize)
  }

  const _countryChange = (value: string) => {
    setSelectedCountryValue(value)
    setCurrentPage(1)
  }

  const fetchRedeemCodeData = async (page, size) => {
    try {
      setLoading(true)
      const response = await getAllRedeemCodes(
        page - 1,
        size,
        selectedCountryValue,
        redeemed,
        subscriptionType
      )
      setTransformedData(response.data)
      setTotalRecords(response.total_items)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateRedeemCode = async () => {
    try {
      const values = await form.validateFields()
      setCreateLoading(true)

      await createRedeemCode(values.providerCountry, values.subscriptionType)

      message.success('Redeem Code Created Successfully')

      setIsCreateModalVisible(false)
      form.resetFields()
      fetchRedeemCodeData(currentPage, pageSize)
    } catch (error) {
      console.error(error)
    } finally {
      setCreateLoading(false)
    }
  }

  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = [
        { label: 'All', value: null },
        ...data.data.map((item) => ({
          label: item.countryName,
          value: item.countryCode,
        })),
      ]
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }
  useEffect(() => {
    fetchCountry()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [subscriptionType, redeemed, selectedCountryValue])

  useEffect(() => {
    fetchRedeemCodeData(currentPage, pageSize)
  }, [pageSize, currentPage, subscriptionType, redeemed, selectedCountryValue])

  const columns = [
    {
      title: 'Redeem Codes',
      dataIndex: 'redeemCode',
      key: 'redeemCode',
    },
    {
      title: 'Country',
      dataIndex: 'providerCountry',
      key: 'providerCountry',
      render: (value: string) => {
        const country = countries.find((c) => c.value === value)
        return country ? country.label : value
      },
    },
    {
      title: 'Subscription Type',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      render: (value: string) => {
        const type = REDEEMCODESUBSCRIPTIONTYPE.find((c) => c.value === value)
        return type ? type.label : value
      },
    },
    {
      title: 'Redeem Status',
      dataIndex: 'redeemed',
      key: 'redeemed',
      render: (value) =>
        value ? (
          <Tag color="green">Redeemed</Tag>
        ) : (
          <Tag color="red">Not Redeemed</Tag>
        ),
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (text) => isoToTimestamp(text),
    },
    {
      title: 'Create Date',
      dataIndex: 'createdOn',
      key: 'createdOn',
      render: (text) => isoToTimestamp(text),
    },
  ]

  return (
    <>
      <Spin spinning={loading}>
        <Title level={3}>Redeem Codes</Title>

        <Row justify="space-between" align="middle" style={{ marginTop: 20 }}>
          <Col>
            <Select
              value={selectedCountryValue}
              style={{ width: 210 }}
              onChange={_countryChange}
              options={countries}
              loading={loading}
            />

            <Select
              placeholder="Subscription Type"
              value={subscriptionType}
              style={{ marginLeft: 10, width: 150 }}
              onChange={(value) => {
                setSubscriptionType(value)
                setCurrentPage(1)
              }}
            >
              {REDEEMCODESUBSCRIPTIONTYPE.map((type) => (
                <Select.Option value={type.value} key={type.value}>
                  {type.label}
                </Select.Option>
              ))}
            </Select>

            <Select
              placeholder="Is Redeemed"
              style={{ marginLeft: 10, width: 150 }}
              value={redeemed}
              onChange={(value) => {
                setRedeemed(value)
                setCurrentPage(1)
              }}
            >
              {REDEEMFLAG.map((flag) => (
                <Select.Option value={flag.value} key={flag.value}>
                  {flag.label}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col>
            <Button
              type="primary"
              onClick={() => setIsCreateModalVisible(true)}
              className="btn-primary"
            >
              + Add Redeem Code
            </Button>
          </Col>
        </Row>

        <DndProvider backend={HTML5Backend}>
          <Table
            rowKey="id"
            dataSource={transformedData}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              total: totalRecords,
            }}
            style={{ marginTop: 30 }}
            onChange={handleTableChange}
          />
        </DndProvider>
      </Spin>
      <Modal
        visible={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="40%"
        style={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
        bodyStyle={{
          padding: 0,
          textAlign: 'center',
        }}
      />
      <Modal
        title="Create Redeem Code"
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onOk={handleCreateRedeemCode}
        confirmLoading={createLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="providerCountry"
            label="Country"
            rules={[{ required: true, message: 'Please select country' }]}
          >
            <Select options={countries.filter((c) => c.value !== null)} />
          </Form.Item>

          <Form.Item
            name="subscriptionType"
            label="Subscription Type"
            rules={[
              { required: true, message: 'Please select subscription type' },
            ]}
          >
            <Select>
              {REDEEMCODESUBSCRIPTIONTYPE.filter((c) => c.value !== '').map(
                (type) => (
                  <Select.Option value={type.value} key={type.value}>
                    {type.label}
                  </Select.Option>
                )
              )}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default RedeemCodePage
